import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Alert, Button } from '@mui/material';
import { GET_USER_BILLING } from '../../graphql/billingQueries';
import { useActivePaymentProvider } from '../../hooks/useActivePaymentProvider';
import LoadingState from '../ui/LoadingState';
import PageContainer from '../ui/PageContainer';
import PageHeader from '../ui/PageHeader';
import { summarizeBilling } from './billingModel';
import type { BillingInfo } from './billingTypes';
import PaymentMethodSection, { usePaymentPortal } from './PaymentMethodSection';

/**
 * /billing/payment-methods: the card behind the subscription, on its own
 * page. Plan and billing shows the same section; this page stays for links
 * that land here (Stripe's portal return, and the past-due redirect from the
 * plan picker, which explains itself through `location.state.notice`).
 */
const PaymentMethods: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Set by Plan and billing when it sends a past-due subscriber here to fix their card.
  const redirectNotice = (location.state as { notice?: unknown } | null)?.notice;
  const { provider, loading: providerLoading, refetch: refetchProvider } = useActivePaymentProvider();

  const {
    data,
    loading: billingLoading,
    error: billingError,
    refetch: refetchBilling,
  } = useQuery(GET_USER_BILLING, {
    // 'cache-first' showed a stale card after the customer updated it in the
    // provider's portal and came back.
    fetchPolicy: 'cache-and-network',
  });
  const billing: BillingInfo | undefined = data?.getUserBilling ?? undefined;
  const summary = useMemo(() => summarizeBilling(billing, []), [billing]);
  const portal = usePaymentPortal({ provider, billing, returnPath: '/billing/payment-methods' });
  const seePlans = () => navigate('/billing', { state: { openPlanPicker: true } });

  return (
    <PageContainer width="narrow">
      <PageHeader
        title="Payment method"
        subtitle="Cards are held by our payment provider. PithPlay never stores your card number."
        backTo={{ to: '/billing', label: 'Plan and billing' }}
      />

      {typeof redirectNotice === 'string' && redirectNotice && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {redirectNotice}
        </Alert>
      )}

      {billingLoading && !billing ? (
        <LoadingState variant="list" count={1} label="Loading payment method" />
      ) : billingError && !billing ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetchBilling()}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your billing details. Check your connection and try again.
        </Alert>
      ) : (
        <PaymentMethodSection
          billing={billing}
          hasLiveSubscription={summary.hasLiveSubscription}
          provider={provider}
          providerLoading={providerLoading}
          onRetryProvider={refetchProvider}
          portal={portal}
          onSeePlans={seePlans}
          title={null}
        />
      )}
    </PageContainer>
  );
};

export default PaymentMethods;
